/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
} from 'react';
import { useDispatch, batch } from 'react-redux';
import PropTypes from 'prop-types';

import { ErrorBoundary } from 'apps/web_forms/bugsnagClient';
import { getIdWithoutSuffix } from 'apps/web_forms/utils/inputsUtils';

import { FormInputsContext } from 'apps/web_forms/contexts/FormInputsContext';
import { setRootLevelFieldsBulk, addRepeatGroupBulk } from 'apps/web_forms/slices/formSlice';
import { ErrorBoundaryContext } from 'apps/web_forms/components/ErrorBoundaryContext';
import ErrorFallback from '../commons/ErrorFallback';

import TextInput from 'apps/web_forms/components/inputs/TextInput';
import BooleanInput from 'apps/web_forms/components/inputs/BooleanInput';
import SelectInput from 'apps/web_forms/components/inputs/SelectInput';
import NumberInput from 'apps/web_forms/components/inputs/NumberInput';
import CalculateInput from 'apps/web_forms/components/inputs/CalculateInput';
import DateTimeInput from 'apps/web_forms/components/inputs/DateTimeInput';
import DateInput from 'apps/web_forms/components/inputs/DateInput';
import TimeInput from 'apps/web_forms/components/inputs/TimeInput';
import LocationInput from 'apps/web_forms/components/inputs/LocationInput';
import EmailInput from 'apps/web_forms/components/inputs/EmailInput';
import PhoneNumberInput from 'apps/web_forms/components/inputs/PhoneNumberInput';
import PasswordInput from 'apps/web_forms/components/inputs/PasswordInput';
import ImageInput from 'apps/web_forms/components/inputs/ImageInput';
import CanvasInputs from 'apps/web_forms/components/inputs/CanvasInputs';
import BarcodeInput from 'apps/web_forms/components/inputs/BarcodeInput';
import ResourceInput from 'apps/web_forms/components/inputs/ResourceInput';
import SubForm from 'apps/web_forms/components/Form/SubForm';
import RepeatGroup from 'apps/web_forms/components/Form/RepeatGroup';
import DriverLicenseInput from 'apps/web_forms/components/inputs/DriverLicenseInput';
import NfcInput from '../inputs/NfcInput';

const inputMap = new Map([
  ['text', TextInput],
  ['boolean', BooleanInput],
  ['select', SelectInput],
  ['integer', NumberInput],
  ['decimal', NumberInput],
  ['calculated', CalculateInput],
  ['datetime', DateTimeInput],
  ['date', DateInput],
  ['time', TimeInput],
  ['location', LocationInput],
  ['email', EmailInput],
  ['phone_number', PhoneNumberInput],
  ['password', PasswordInput],
  ['image', ImageInput],
  ['signature', CanvasInputs],
  ['sketch', CanvasInputs],
  ['barcode', BarcodeInput],
  ['resource', ResourceInput], // "Attached File"
  ['license', DriverLicenseInput],
  ['nfc', NfcInput],
]);

const FormInputs = ({
  elements,
  dispatchInitialAnswers,
  setValue,
  onChildChangeValue,
  isModal = false,
  isInsideGroupModal = false,
  showContent = true,
}) => {
  const repeatGroupsGenerated = useRef(false);
  const [pendingUpdates, setPendingUpdates] = useState([]);
  const [pendingActiveRulesUpdates, setPendingActiveRulesUpdates ] = useState([])

  const dispatch = useDispatch();

  const { evaluateExpressionChanges, formContext } =
    useContext(FormInputsContext);

  const { trigger: triggerValidations } = formContext;

  useEffect(() => {
    // Trigger validations on form load

    // setTimeout is being used to trigger validations after the render ends
    // so all field values are applied
    setTimeout(triggerValidations);
  }, []);

  //Changing the logic of onChangeValue to batch the updates so that
  //the validations are triggered only once ideally after all the updates are done
  const onChangeValue = useCallback((identifier, value, isDirty) => {
    setPendingUpdates((prev) => [...prev, { identifier, value, isDirty }]);
    if(isInsideGroupModal){
      //This check is used to trigger progression circle calculation for inputs inside Group/Subform
      onChildChangeValue(identifier, value)
    }
  }, []);

  useEffect(() => {
    if (pendingUpdates.length > 0) {
      batch(() => {
        pendingUpdates.forEach(({ identifier, value, isDirty }) => {
          setValue(identifier, value, { shouldDirty: isDirty });
          evaluateExpressionChanges(identifier);
        });
        //Moving triggerValidations inside the batch update for both consistency of operations and performance
        setTimeout(triggerValidations);
      });

      setPendingUpdates([]);
    }
  }, [pendingUpdates]);

  const toggleActiveValue = useCallback((identifier, isActive) => {
    setPendingActiveRulesUpdates((prev) => [...prev, { identifier, isActive}]);
  }, []);

  useEffect(() => {
    if (pendingActiveRulesUpdates.length > 0) {
      dispatch(setRootLevelFieldsBulk(pendingActiveRulesUpdates));
      setPendingActiveRulesUpdates([]);
    }
  }, [pendingActiveRulesUpdates]);

  useEffect(() => {
    if (repeatGroupsGenerated.current) return;
    const repeatGroups = elements.filter((ele) => {
      if (ele.children && !ele.maxOccurs && !ele.minOccurs) {
        return ele;
      }
    });

    repeatGroupsGenerated.current = true; // Prevents infinite loop

    if (repeatGroups.length === 0) return;

    dispatch(addRepeatGroupBulk(repeatGroups));
  }, [elements]);

  return (
    <>
      <div className='elements-container'>
        {elements.map((question) => {
          let InputComponent = inputMap.get(question.type);

          const additionalProps = {};

          if (question.type === 'group') {
            const groupIdentifier = getIdWithoutSuffix(question.identifier);

            if (
              dispatchInitialAnswers &&
              dispatchInitialAnswers[groupIdentifier]
            ) {
              additionalProps.dispatchInitialAnswers =
                dispatchInitialAnswers[groupIdentifier];
            }

            if (!isModal) {
              if (question.maxOccurs && question.minOccurs) {
                InputComponent = SubForm;
              } else {
                InputComponent = RepeatGroup;
              }
            }
          }

          if (!InputComponent) return null;

          return (
            <ErrorBoundaryContext.Provider
              value={question.identifier}
              key={question.identifier}
            >
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <InputComponent
                  element={question}
                  onChangeValue={onChangeValue}
                  toggleActiveValue={toggleActiveValue}
                  formContext={formContext}
                  dispatchInitialAnswers={question.dispatchInitialAnswers}
                  showContent={showContent}
                  {...additionalProps}
                />
              </ErrorBoundary>
            </ErrorBoundaryContext.Provider>
          );
        })}
      </div>
    </>
  );
};

FormInputs.propTypes = {
  setValue: PropTypes.func.isRequired,
  dispatchInitialAnswers: PropTypes.shape({}),
  elements: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

FormInputs.defaultProps = {
  dispatchInitialAnswers: undefined,
};

export default React.memo(FormInputs);
